<script>
  import { onMount } from "svelte";
  import { currentLang, t } from "../lib/i18n";
  import { gsap } from "gsap";
  import { ScrollTrigger } from "gsap/ScrollTrigger";
  gsap.registerPlugin(ScrollTrigger);

  let animationPlayed = false;
  let heroSection;
  let nextSection;

  function scrollToNextSection() {
    const nextSection = document.querySelector(".services-section") || document.querySelector("#services");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({
        top: window.innerHeight,
        behavior: "smooth",
      });
    }
  }

  onMount(() => {

    if (!animationPlayed) {
      const tl = gsap.timeline();

      tl.from(".hero-content h1", {
        y: 50,
        opacity: 0,
        duration: 0.8,
        delay: 0.2,
      })
        .from(
          ".hero-content p",
          {
            y: 30,
            opacity: 0,
            duration: 0.6,
          },
          "-=0.3",
        )
        .from(
          ".hero-content .btn",
          {
            y: 20,
            opacity: 0,
            duration: 0.5,
          },
          "-=0.2",
        )
        .from(
          ".hero-overlay",
          {
            opacity: 0.3,
            duration: 0.8,
          },
          "-=0.5",
        );

      animationPlayed = true;
    }

    // Set up parallax effect
    ScrollTrigger.create({
      trigger: ".hero",
      start: "top top",
      end: "bottom top",
      scrub: true,
      onUpdate: (self) => {
        gsap.to(".hero-background", {
          y: self.progress * 200,
          ease: "none",
        });
      },
    });
  });
</script>

<section class="hero" bind:this={heroSection}>
  <div class="hero-background"></div>
  <div class="hero-overlay"></div>
  <div class="container hero-container">
    <div class="hero-content">
      <h1>{t("hero.welcome", $currentLang)}</h1>
      <p>{t("hero.subtitle", $currentLang)}</p>
      <button class="btn btn-primary">{t("hero.cta", $currentLang)}</button>
    </div>

    <div
      class="scroll-down-indicator"
      on:click={scrollToNextSection}
      on:keydown={(e) => e.key === "Enter" && scrollToNextSection()}
      tabindex="0"
      role="button"
      aria-label={$currentLang === "hu" ? "Görgessen lefelé" : "Scroll down"}
    >
      <div class="mouse">
        <div class="wheel"></div>
      </div>
      <div class="scroll-arrows">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  </div>
</section>

<style>
  .hero {
    height: 100vh;
    height: 100svh;
    min-height: 100svh;
    max-height: 100svh;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    overflow: hidden;
  }

  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center 20%;
    background-repeat: no-repeat;
    z-index: 0;
    background-image: url("/images/airport-car.webp");
  }

  .hero-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 1;
  }

  .hero-container {
    position: relative;
    z-index: 2;
    padding: 0 2rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .hero-content {
    text-align: center;
    z-index: 2;
    max-width: 1200px;
    padding: 0 2rem;
  }

  .hero-content h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hero-content p {
    font-size: 1.5rem;
    margin-bottom: 2rem;
    line-height: 1.4;
  }

  .hero-content .btn {
    font-size: 1.1rem;
    padding: 1rem 2.5rem;
    font-weight: 600;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    /* Assuming you have CSS variables for primary colors */
    background-color: var(--primary);
    color: white;
    border: none;
    border-radius: 5px; /* Add some border-radius */
    cursor: pointer;
  }

  .hero-content .btn:hover,
  .hero-content .btn:focus {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    background-color: var(--primary-dark); /* Use a darker shade for hover */
    outline: none;
  }

  .scroll-down-indicator {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    z-index: 10;
    opacity: 0;
    animation: fadeInIndicator 1s ease-out 2s forwards;
  }

  @keyframes fadeInIndicator {
    from {
      opacity: 0;
      transform: translate(-50%, -10px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }

  .scroll-down-indicator:hover {
    opacity: 1;
  }

  .mouse {
    width: 26px;
    height: 42px;
    border: 2px solid white;
    border-radius: 20px;
    display: flex;
    justify-content: center;
    padding-top: 8px;
    margin-bottom: 5px;
  }

  .wheel {
    width: 4px;
    height: 10px;
    background: rgb(253, 251, 238);
    border-radius: 2px;
    animation: mouse-scroll 1.6s cubic-bezier(0.15, 0.41, 0.69, 0.94) infinite;
  }

  .scroll-arrows span {
    display: block;
    width: 8px;
    height: 8px;
    border-bottom: 2px solid white;
    border-right: 2px solid white;
    transform: rotate(45deg);
    margin: -3px auto 0;
    animation: arrow-scroll 1.6s cubic-bezier(0.15, 0.41, 0.69, 0.94) infinite;
  }

  .scroll-arrows span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .scroll-arrows span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes mouse-scroll {
    0% {
      opacity: 0;
      transform: translateY(-5px);
    }
    20% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(15px);
    }
  }

  @keyframes arrow-scroll {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes subtle-zoom {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(1.05);
    }
  }

  /* Responsive adjustments */
  @media screen and (max-width: 768px) {
    .hero {
      height: 100svh;
      min-height: 100svh;
      max-height: 100svh;
    }
    .hero-content h1 {
      font-size: 2.2rem;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }

    .hero-content p {
      font-size: 1.1rem;
    }

    .scroll-down-indicator {
      bottom: 18px;
    }
  }

  @media screen and (max-width: 480px) {
    .hero {
      height: 100svh;
      min-height: 100svh;
      max-height: 100svh;
    }
    .hero-content h1 {
      font-size: 1.85rem;
    }

    .hero-content p {
      font-size: 0.95rem;
      margin-bottom: 1.5rem;
    }

    .hero-content .btn {
      font-size: 0.95rem;
      padding: 0.75rem 1.75rem;
    }

    .scroll-down-indicator {
      bottom: 15px;
    }
  }

  /* IMPORTANT: Ensure the section *immediately following* the hero has a background color */
  /* This is crucial for the sticky background to appear to slide behind it. */
  /* Example CSS for your next section (add this to your global styles or the next section's component) */
  /*
  .your-next-section-class {
      background-color: rgb(253, 251, 238); // Or any solid color
      position: relative; // Often helpful for z-index stacking
      z-index: 1; // Ensure it's above the fixed hero background (z-index: 0)
  }
  */

  .discount-popup {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.5s ease-out;
  }

  .discount-popup-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("/images/car-wash.jpg");
    background-size: cover;
    background-position: center;
    z-index: -1;
  }

  .discount-popup-background::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(63, 0, 113, 0.9) 0%,
      rgba(63, 0, 113, 0.7) 50%,
      rgba(63, 0, 113, 0.5) 100%
    );
  }

  .discount-popup-content {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    padding: 2rem;
    border-radius: 15px;
    text-align: center;
    max-width: 500px;
    width: 90%;
    position: relative;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .discount-popup-content h2 {
    font-size: 2.5rem;
    color: white;
    margin-bottom: 1rem;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  .discount-popup-content p {
    color: white;
    font-size: 1.2rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }

  .close-popup {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0.5rem;
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }

  .close-popup:hover {
    opacity: 1;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media screen and (max-width: 768px) {
    .discount-popup-content {
      padding: 1.5rem;
    }

    .discount-popup-content h2 {
      font-size: 2rem;
    }

    .discount-popup-content p {
      font-size: 1rem;
    }
  }
</style>
